/**
 * UpkeepMonitoringPage
 * 
 * Main page component for Upkeep Budget Monitoring module
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    HStack,
    useToast,
} from '@chakra-ui/react';
import Navbar from '../../../../components/Navbar';
import { useUpkeepMonitoring } from '../hooks/useUpkeepMonitoring';
import FilterUpkeepMonitoring from '../components/FilterUpkeepMonitoring';
import UpkeepMonitoringTable from '../components/UpkeepMonitoringTable';
import UpkeepMonitoringApiService from '../services/upkeepMonitoringApiService';
import { UpkeepBudgetFilters } from '../types';

/**
 * Main Upkeep Monitoring Page Component
 */
const UpkeepMonitoringPage: React.FC = () => {
    const toast = useToast();

    const {
        displayRows,
        loading,
        filters,
        setFilters,
        fetchData,
    } = useUpkeepMonitoring();

    const [isExporting, setIsExporting] = useState(false);

    /**
     * Fetch data on initial load
     */
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Handle filter changes
     */
    const handleFilterChange = useCallback(
        (newFilters: UpkeepBudgetFilters) => {
            setFilters(newFilters);
        },
        [setFilters]
    );

    /**
     * Handle clear filters
     */
    const handleClearFilters = useCallback(() => {
        toast({
            title: 'Filter direset',
            description: 'Semua filter telah dikembalikan ke nilai default',
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
        });
    }, [toast]);

    /**
     * Handle apply filters - accepts filters as parameter to avoid stale state
     */
    const handleApplyFilters = useCallback((filtersToApply: UpkeepBudgetFilters) => {
        setFilters(filtersToApply);
        // Fetch data with the new filters immediately
        fetchData(filtersToApply);
        toast({
            title: 'Filter diterapkan',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
        });
    }, [setFilters, fetchData, toast]);

    /**
     * Handle export to Excel via Backend Streaming
     */
    const handleExport = useCallback(async () => {
        if (!displayRows || displayRows.length === 0) {
            toast({
                title: 'Tidak ada data',
                description: 'Tidak ada data untuk diekspor',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
            return;
        }

        try {
            setIsExporting(true);
            toast({
                title: 'Mengekspor data',
                description: 'Sedang membuat file Excel (Summary & Detail Per Blok)...',
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });

            await UpkeepMonitoringApiService.downloadUpkeepBudgetExcel(filters);

            toast({
                title: 'Export berhasil',
                description: 'Data berhasil diekspor ke Excel (Summary & Detail Per Blok)',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
        } catch (err: any) {
            toast({
                title: 'Export gagal',
                description: err.message || 'Terjadi kesalahan saat mengekspor data',
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setIsExporting(false);
        }
    }, [displayRows, filters, toast]);

    /**
     * Handle refresh data
     */
    const handleRefresh = useCallback(() => {
        fetchData();
        toast({
            title: 'Data diperbarui',
            description: 'Data budget monitoring telah dimuat ulang',
            status: 'info',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
        });
    }, [fetchData, toast]);

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />

            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                    {/* Page Header */}
                    <VStack align="start" spacing={1}>
                        <Heading size="lg" color="customGreen.500">
                            Upkeep Budget Monitoring
                        </Heading>
                        <HStack spacing={4} flexWrap="wrap">
                            <Text color="gray.600" fontSize="sm">
                                Monitoring penggunaan budget upkeep
                            </Text>
                        </HStack>
                    </VStack>

                    {/* Filter Section */}
                    <FilterUpkeepMonitoring
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onApplyFilters={handleApplyFilters}
                        onExport={handleExport}
                        onRefresh={handleRefresh}
                        isExporting={isExporting}
                    />

                    {/* Data Table */}
                    <UpkeepMonitoringTable data={displayRows} loading={loading} />
                </VStack>
            </Box>
        </Box>
    );
};

export default UpkeepMonitoringPage;
